import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  IconButton,
  Tabs,
  Tab,
  LinearProgress,
  useTheme,
  alpha,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Tooltip,
  Switch,
  Collapse,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Science,
  Analytics,
  CheckCircle,
  Assignment,
  Timer,
  PriorityHigh,
  TrendingUp,
  Assessment,
  Warning,
  Settings,
  BarChart,
  Timeline,
  Tune,
  Add,
  Delete,
  Save,
  PlayArrow,
  Stop,
  Refresh,
  CloudUpload,
  GetApp,
  Error,
  Send,
  Visibility,
  Edit,
  FileUpload,
  AutoAwesome,
  TipsAndUpdates,
  ExpandMore,
  Close,
  ExpandLess,
  LocalFireDepartment,
  Search,
  Clear,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';
import apiService from '../../services/api';
import SampleQcStatusChip from '../qc/SampleQcStatusChip';

// Enhanced interfaces for comprehensive AU analysis (identical to SAA)
interface PreparationData {
  initialWeight: number;
  digestionMethod: string;
  initialVolume: number;
  dilutions: Array<{
    volumeTaken: number;
    finalVolume: number;
    dilutionFactor: number;
  }>;
  totalDilutionFactor: number;
  dilution_factor?: number; // Alternative name for compatibility
  vf1?: number;
  vp1?: number;
  preparation_completed_at: string;
  technician_id: string;
  technician_name?: string;
}

interface AnalysisReading {
  element: string;
  absorbance?: number; // Store original lecture value
  grams_per_tonne: number;
  ppm: number;
  uncertainty: number;
  timestamp: string;
  instrument_settings: {
  method: string;
    initial_volume?: number;
    initial_weight?: number;
    dilution_factor?: number;
    temperature?: number;
  };
  qc_results: {
    blank_correction: number;
    recovery_percent: number;
    rsd_percent: number;
    status: 'PASS' | 'FAIL' | 'WARNING';
  };
}

interface AUAnalysisForm {
  sample_id: string;
  preparation_data: PreparationData | null;
  analysis_readings: AnalysisReading[];
  instrument_conditions: {
  method_used: string;
    furnace_temperature: number;
    fusion_time: number;
    cupellation_temperature: number;
    cupellation_time: number;
  };
  notes: string;
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'VALIDATED';
}

interface BatchAnalysisData {
  [sampleId: string]: {
    [element: string]: {
      lecture?: number;
      grams_per_tonne: number;
      ppm: number;
      timestamp: string;
      originalReading?: number;
      calculatedFromFormula?: boolean;
    };
  };
}

interface MethodValidation {
  element: string;
  lod_ppm: number;
  loq_ppm: number;
  recovery_min_percent: number;
  recovery_max_percent: number;
  expanded_uncertainty_percent: number;
}

interface AUFileInfo {
  filename: string;
  path: string;
  size: number;
  modified: Date;
  matchedSamples: string[];
}

const AUAnalystDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  
  // Main state
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzingBatch, setAnalyzingBatch] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Individual sample state
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [analysisDialog, setAnalysisDialog] = useState(false);
  
  // Batch analysis state
  const [selectedBatch, setSelectedBatch] = useState<{batchNumber: string, samples: Sample[]} | null>(null);
  const [batchAnalysisDialog, setBatchAnalysisDialog] = useState(false);
  const [batchAnalysisData, setBatchAnalysisData] = useState<BatchAnalysisData>({});
  const [sampleDilutionChoices, setSampleDilutionChoices] = useState<Record<string, Record<string, 0 | 1 | 2>>>({});
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  
  // Method validation and quality control
  const [methodValidations, setMethodValidations] = useState<MethodValidation[]>([]);

  // Individual analysis data state
  const [analysisData, setAnalysisData] = useState<AUAnalysisForm>({
    sample_id: '',
    preparation_data: null,
    analysis_readings: [],
    instrument_conditions: {
      method_used: 'FIRE_ASSAY',
      furnace_temperature: 1050,
      fusion_time: 60,
      cupellation_temperature: 950,
      cupellation_time: 45,
    },
    notes: '',
    status: 'DRAFT',
  });

  // AU File Import System
  const [importLoading, setImportLoading] = useState(false);
  const [availableFiles, setAvailableFiles] = useState<AUFileInfo[]>([]);
  const [fileImportDialogOpen, setFileImportDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [forceRender, setForceRender] = useState(0);
  const [sendingToPrepBatch, setSendingToPrepBatch] = useState(false); // 🔥 NEW: Send to prep loading state

  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    ready_for_analysis: 0,
    in_progress: 0,
    completed_today: 0,
    urgent_samples: 0,
  });

  // 🚀 PAGINATION & SEARCH STATE
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(250);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [totalBatches, setTotalBatches] = useState(0);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load data when search changes
  useEffect(() => {
    loadAUAnalysisData();
  }, [debouncedSearch]);

  // 🚀 PERFORMANCE FIX: Load method validations only once on mount
  useEffect(() => {
    loadMethodValidations();
  }, []);

  // 🔒 SEPARATE EFFECT: Auto-refresh with modal state checks (but doesn't reload when modals open)
  useEffect(() => {
    const interval = setInterval(() => {
      // Don't refresh if any modal is open
      if (!batchAnalysisDialog && !analysisDialog && !fileImportDialogOpen) {
        loadAUAnalysisData();
      }
    }, 120000); // 2 minutes auto-refresh
    return () => clearInterval(interval);
  }, [batchAnalysisDialog, analysisDialog, fileImportDialogOpen]);


  // Real-time calculation function for AU analysis (identical to SAA)
  const calculateFromLecture = useCallback((
    readingValue: number, // Raw reading from instrument
    sampleId: string,
    element: string,
    prepData: any
  ): { grams_per_tonne: number; ppm: number } => {
    if (readingValue <= 0 || !prepData) {
      return { grams_per_tonne: 0, ppm: 0 }; // Fallback
    }

    const sampleDilutionChoice = sampleDilutionChoices[sampleId]?.[element] ?? 0;
    const V = prepData.initialVolume || 100; // Initial volume in mL
    const p = prepData.initialWeight || 0.5; // Initial weight in grams
    const vf = prepData.finalVolume || prepData.dilutions?.[0]?.finalVolume || 1; // Volume final
    const vp = prepData.volumeTaken || prepData.dilutions?.[0]?.volumeTaken || 1; // Volume prélevé
    const vf1 = prepData.vf1 || 1; // Volume final alternatif
    const vp1 = prepData.vp1 || 1; // Volume prélevé alternatif

    let grams_per_tonne = 0;
    if (sampleDilutionChoice === 0) {
      // Simplified formula: g/t = Reading × (V/p)
      grams_per_tonne = (readingValue * V) / p;
    } else if (sampleDilutionChoice === 1) {
      // Complete formula: g/t = Reading × (V/p) × (vf/vp)
      grams_per_tonne = (readingValue * V * vf) / (p * vp);
    } else if (sampleDilutionChoice === 2) {
      // Alternative complete formula: g/t = Reading × (V/p) × (vf1/vp1)
      grams_per_tonne = (readingValue * V * vf1) / (p * vp1);
    }

    // Convert g/t to ppm (1 g/t = 1 ppm for precious metals)
    const ppm = grams_per_tonne;

    return { grams_per_tonne, ppm };
  }, [sampleDilutionChoices]);

  // Helper function to get elements for a sample - checks element_lab_routing
  // 🔧 FIX: Must filter by BOTH selected_elements AND element_lab_routing
  const getElementsForSample = (sample: any): string[] => {
    const isBGMSample = sample?.metadonnees?.bgm_method_selected === true;
    
    // For BGM samples, only Au (BGM has fixed routing: Au → AU lab)
    if (isBGMSample) {
      return ['Au'];
    }
    
    // 🔧 CRITICAL FIX: Start with selected_elements, then filter by routing
    // This ensures elements removed per-sample are properly greyed out
    const selectedElements = sample?.metadonnees?.selected_elements || [];
    const elementRouting = sample?.metadonnees?.element_lab_routing || {};
    const isMultiLabSample = sample?.metadonnees?.multi_lab_workflow === true ||
                             Object.keys(elementRouting).length > 0;
    
    if (isMultiLabSample && selectedElements.length > 0) {
      // Filter selected_elements to only those routed to AU lab
      const auElements = selectedElements.filter((element: string) => 
        elementRouting[element] === 'LABORATOIRE_AU'
      );
      
      if (auElements.length > 0) {
        console.log(`🔍 AU Elements for ${sample?.sample_id} (filtered by selected_elements):`, auElements);
        return auElements;
      }
    }
    
    // Fallback for regular AU samples (no custom routing)
    // 🔥 FIX: Support all elements that can be analyzed in AU lab (not just Au/Ag)
    // AU/Fire Assay lab can analyze: Au, Ag, Zn, Fe, Cu, Pb, Ni, Co, Mn, Cd, As, Sb, Bi, Se, Te
    const validAuLabElements = ['Au', 'Ag', 'Zn', 'Fe', 'Cu', 'Pb', 'Ni', 'Co', 'Mn', 'Cd', 'As', 'Sb', 'Bi', 'Se', 'Te'];
    const auElements = selectedElements.filter((el: string) => validAuLabElements.includes(el));
    
    // If no AU elements were found, default to ['Au'] only for samples in AU workflow
    return auElements.length > 0 ? auElements : ['Au'];
  };

  // 🔧 FIX: Get ALL unique elements across entire batch (union of all samples)
  // This ensures table headers show all possible elements even if samples have different selections
  const getAllBatchElements = (batchSamples: any[]): string[] => {
    const allElements = new Set<string>();
    batchSamples.forEach(sample => {
      const sampleElements = getElementsForSample(sample);
      sampleElements.forEach(el => allElements.add(el));
    });
    // Sort: Au first, then Ag, then others alphabetically
    const result = Array.from(allElements).sort((a, b) => {
      if (a === 'Au') return -1;
      if (b === 'Au') return 1;
      if (a === 'Ag') return -1;
      if (b === 'Ag') return 1;
      return a.localeCompare(b);
    });
    return result;
  };

  const loadAUAnalysisData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // 🔥 PERFORMANCE FIX: Query only relevant statuses for AU analysis
      // Increased limit to 50000 to handle the growing volume of BGM samples in upstream
      // ICP statuses. The deployed bundle was already at 50000 before local source caught
      // up — this brings them back in parity (drift reconciliation, 2026-05-05).
      const response = await samplesService.getSamples(1, 50000, {
        status: [
          SampleStatus.LABORATOIRE_AU_ANALYSE,        // Regular AU samples
          SampleStatus.VALIDATION_SERVICE,            // Multi-lab samples in ICP/SAA validation
          SampleStatus.PREPARATION_MECANIQUE,         // Multi-lab waiting for prep
          SampleStatus.LABORATOIRE_ICP_ANALYSE,       // Multi-lab in ICP workflow
          SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE, // Multi-lab samples still in ICP prep but AU prep done
          SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE, // 🔧 FIX: Added Physico base status
          SampleStatus.LABORATOIRE_PHYSICO_CHIMIQUE_ANALYSE, // 🔧 FIX: Added Physico analysis status
        ]
      });
      const allSamples = Array.isArray(response) ? response : (response.samples || []);
      
      const auSamples = allSamples.filter((sample: any) => {
        // Regular AU samples
        const isRegularAU = sample.statut === 'LABORATOIRE_AU_ANALYSE';
        
        // 🔬 Multi-lab samples (BGM or custom routing) - should appear when they have completed AU preparation AND ready for analysis
        const isMultiLabSample = sample.metadonnees?.multi_lab_workflow === true || 
                                 sample.metadonnees?.bgm_method_selected === true;
        const selectedElements = sample.metadonnees?.selected_elements || [];
        const elementRouting = sample.metadonnees?.element_lab_routing || {};
        // 🔧 FIX: Check if ANY selected element routes to AU (not just elementRouting values)
        const hasAuElements = selectedElements.some((element: string) => 
          elementRouting[element] === 'LABORATOIRE_AU'
        );
        // 🔧 FIX: Also check laboratories_involved as fallback
        const hasAuInLabs = sample.laboratories_involved?.includes('LABORATOIRE_AU') || false;
        const auPrepCompleted = sample.metadonnees?.au_preparation_completed === true;
        const auAnalysisCompleted = sample.metadonnees?.au_analysis_completed === true;
        
        // Multi-lab samples appear in AU analyst ONLY when AU prep is done but analysis is not
        const isMultiLabForAU = isMultiLabSample && (hasAuElements || hasAuInLabs) && auPrepCompleted && !auAnalysisCompleted;
        
        // Debug logging for multi-lab samples
        if (isMultiLabSample) {
          console.log(`🔍 AU Analyst Filter Debug - Sample ${sample.sample_id}:`, {
            isMultiLabSample,
            hasAuElements,
            hasAuInLabs,
            auPrepCompleted,
            auAnalysisCompleted,
            shouldShow: isMultiLabForAU,
            currentStatus: sample.statut
          });
        }
        
        return isRegularAU || isMultiLabForAU;
      });
      
      // 🔍 Apply search filter (frontend-side)
      let filteredSamples = auSamples;
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        filteredSamples = auSamples.filter((sample: any) => 
          sample.sample_id?.toLowerCase().includes(searchLower) ||
          sample.metadonnees?.batch_number?.toLowerCase().includes(searchLower) ||
          sample.metadonnees?.batch_info?.batch_number?.toLowerCase().includes(searchLower) ||
          sample.reference_client?.toLowerCase().includes(searchLower)
        );
      }
      
      setSamples(filteredSamples);
      
      // Calculate stats (from filtered samples)
      const ready = filteredSamples.filter((s: any) => {
        if (s.statut === 'LABORATOIRE_AU_ANALYSE') return true;
        const isMultiLab = s.metadonnees?.multi_lab_workflow === true || s.metadonnees?.bgm_method_selected === true;
        return isMultiLab && (s.statut === 'LABORATOIRE_ICP_ANALYSE' || s.statut === 'VALIDATION_SERVICE');
      }).length;
      const urgent = filteredSamples.filter((s: any) => s.priorite === 'URGENTE').length;
      
      setStats({
        ready_for_analysis: ready,
        in_progress: 0,
        completed_today: 0,
        urgent_samples: urgent,
      });
      
    } catch (error) {
      console.error('Error loading AU analysis data:', error);
      setError('Erreur lors du chargement des données AU');
    } finally {
      setLoading(false);
    }
  };

  const loadMethodValidations = async () => {
    try {
      // Mock method validations for AU elements
      const auValidations: MethodValidation[] = [
        {
          element: 'Au',
          lod_ppm: 0.005,
          loq_ppm: 0.015,
          recovery_min_percent: 85,
          recovery_max_percent: 115,
          expanded_uncertainty_percent: 10.0,
        },
        {
          element: 'Ag',
          lod_ppm: 0.1,
          loq_ppm: 0.3,
          recovery_min_percent: 85,
          recovery_max_percent: 115,
          expanded_uncertainty_percent: 12.0,
        }
      ];
      setMethodValidations(auValidations);
    } catch (error) {
      console.error('Error loading method validations:', error);
    }
  };

  // Initialize batch sample data for dilution choices
  const initializeBatchAnalysisData = (samples: Sample[]) => {
    const initialData: BatchAnalysisData = {};
    const initialDilutionChoices: Record<string, Record<string, 0 | 1 | 2>> = {};
    
    samples.forEach(sample => {
      const elementsForSample = getElementsForSample(sample);
      
      // Initialize per-element dilution choices (default to 0 = no dilution)
      initialDilutionChoices[sample.id] = Object.fromEntries(
        elementsForSample.map(element => [element, 0])
      );
      
      initialData[sample.id] = Object.fromEntries(
        elementsForSample.map(element => [
          element, 
          { 
            lecture: 0,
          grams_per_tonne: 0, 
          ppm: 0, 
          timestamp: new Date().toISOString(),
          originalReading: 0,
          calculatedFromFormula: false
        }
        ])
      );
    });
    
    setBatchAnalysisData(initialData);
    setSampleDilutionChoices(initialDilutionChoices);
  };

  // Update batch analysis data for individual elements
  const updateBatchAnalysisData = (sampleId: string, element: string, value: { grams_per_tonne?: number; ppm?: number; timestamp: string }) => {
    setBatchAnalysisData(prev => ({
      ...prev,
      [sampleId]: {
        ...prev[sampleId],
        [element]: {
          ...prev[sampleId]?.[element],
          ...value
        }
      }
    }));
  };

  // Batch handling functions
  const groupSamplesByBatch = (samples: Sample[]) => {
    const batches: Record<string, Sample[]> = {};
    const individual: Sample[] = [];

    samples.forEach(sample => {
      const batchNumber = getBatchNumber(sample);
      if (batchNumber) {
        if (!batches[batchNumber]) {
          batches[batchNumber] = [];
        }
        batches[batchNumber].push(sample);
      } else {
        individual.push(sample);
      }
    });

    return { batches, individual };
  };

  const getBatchNumber = (sample: Sample): string | null => {
    // First try metadata fields
    const metadataBatch = sample.metadonnees?.batch_info?.batch_number || 
                         sample.metadonnees?.batch_number;
    if (metadataBatch) return metadataBatch;
    
    // Fallback: Extract from sample_id (BS-XXXX-YY-ZZ or legacy R-XXXX → BS/R-XXXX)
    const match = sample.sample_id?.match(/^((?:BS|R)-\d+)-/);
    return match ? match[1] : null;
  };

  const getBatchPosition = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.sample_position || 1;
  };

  const getTotalSamplesInBatch = (sample: Sample): number => {
    return sample.metadonnees?.batch_info?.total_samples || 1;
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchNumber)) {
        newSet.delete(batchNumber);
      } else {
        newSet.add(batchNumber);
      }
      return newSet;
    });
  };

  // Handle batch analysis start - 🚀 PERFORMANCE OPTIMIZED
  const handleStartBatchAnalysis = async (batchNumber: string, samples: Sample[]) => {
    setAnalyzingBatch(true);
    // 🚀 REMOVED: Artificial 50ms delay that was causing slowness
    setSelectedBatch({ batchNumber, samples });
    initializeBatchAnalysisData(samples);
    setBatchAnalysisDialog(true);
    setAnalyzingBatch(false);
  };

  // Save batch analysis and move all samples to validation
  const handleCompleteBatchAnalysis = async () => {
    if (!selectedBatch) return;

    try {
      setLoading(true);
      
      
      // Prepare bulk update data
      const bulkUpdateData = selectedBatch.samples.map(sample => {
        const sampleAnalysisData = batchAnalysisData[sample.id] || {};
        const prepData = sample.metadonnees?.au_preparation;
        
        // Construct analysis readings from batch data
        const analysisReadings: AnalysisReading[] = [];
        // 🔥 FIX: Use dynamic elements from sample's AU routing, not hardcoded list
        const elementsForThisSample = getElementsForSample(sample);
        
        elementsForThisSample.forEach((element: string) => {
          const elementData = sampleAnalysisData[element];
          if (elementData && elementData.grams_per_tonne > 0) {
            analysisReadings.push({
              element: element,
              absorbance: elementData.originalReading || elementData.lecture || elementData.grams_per_tonne, // Store original lecture
              grams_per_tonne: elementData.grams_per_tonne,
              ppm: elementData.ppm,
              uncertainty: 10.0, // Default uncertainty for AU
              timestamp: elementData.timestamp || new Date().toISOString(),
              instrument_settings: {
                method: prepData?.digestionMethod || 'AU_ANALYSIS',
                initial_volume: prepData?.initialVolume || 100,
                initial_weight: prepData?.initialWeight || 0.5,
                dilution_factor: prepData?.totalDilutionFactor || 1,
              },
              qc_results: {
                blank_correction: 0,
                recovery_percent: 100,
                rsd_percent: 5,
                status: 'PASS',
              }
            });
          }
        });

        const completedForm = {
          sample_id: sample.sample_id,
          preparation_data: prepData,
          analysis_readings: analysisReadings,
        instrument_conditions: {
          method_used: 'FIRE_ASSAY',
            furnace_temperature: 950, // Default for AU analysis
          fusion_time: 60,
          cupellation_temperature: 950,
            cupellation_time: prepData?.au_cupellation_time || 45,
          },
                      notes: `AU analysis completed for batch ${selectedBatch.batchNumber}`,
          status: 'COMPLETED',
        };

        // 🚨 CRITICAL FIX: ALL samples (BGM and regular) must go to VALIDATION_SERVICE after AU analysis
        // AU Supervisor dashboard filters them by metadata (au_analysis_form, au_analysis_completed)
        // ICP Supervisor dashboard filters them by different metadata (icp_analysis_form, icp_analysis_completed)
        const newStatus = SampleStatus.VALIDATION_SERVICE;

        return {
          id: sample.id,
          metadonnees: {
            ...sample.metadonnees,
            au_analysis_form: completedForm,
            au_analysis_results: analysisReadings, // Store AU results separately
            au_analysis_completed: true,
            au_analysis_completed_at: new Date().toISOString(),
            batch_analysis: true,
            batch_number: selectedBatch.batchNumber,
            // 🚨 CRITICAL FIX: Add missing metadata fields that AU Supervisor needs
            analysis_status: 'COMPLETED',
            batch_analysis_summary: {
              batch_number: selectedBatch.batchNumber,
              is_batch_sample: true,
              elements_analyzed: elementsForThisSample,
              total_readings: analysisReadings.length,
              direct_ppm_input: true,
              calibration_standard: 'AU Fire Assay Standard',
              detection_method: 'Fire Assay - AU Analysis',
              completion_date: new Date().toISOString(),
              imported_file: undefined // AU doesn't typically use imported files
            },
            // 🔧 CRITICAL FIX: MERGE AU data with existing ICP/SAA data (don't overwrite!)
            analysis_results: [
              // Preserve existing ICP/SAA data (filter out any old AU lab elements to avoid duplicates)
              ...(sample.metadonnees?.analysis_results || []).filter(r => 
                !elementsForThisSample.includes(r.element)
              ),
              // Add new AU data
              ...analysisReadings.map(reading => ({
                element: reading.element,
                intensity: 0,
                timestamp: reading.timestamp,
                uncertainty: reading.uncertainty || 10,
                detection_status: 'VALID',
                concentration_ppm: reading.ppm,
                concentration_final_percent: reading.ppm / 10000 // Convert ppm to %
              }))
            ]
          },
          status: newStatus,
          commentaires: `Analyse AU LOT terminée - Batch: ${selectedBatch.batchNumber}, ${analysisReadings.length} éléments analysés`
        };
      });

      // Use bulk update API
      const sampleIds = bulkUpdateData.map(item => item.id);
      const metadataUpdates = Object.fromEntries(
        bulkUpdateData.map(item => [item.id, item.metadonnees])
      );
      // For mixed batches with BGM samples, update each sample individually
      const hasBGMSamples = bulkUpdateData.some(item => 
        selectedBatch.samples.find(s => s.id === item.id)?.metadonnees?.bgm_method_selected === true
      );
      
      let result;
      if (hasBGMSamples) {
        // Update samples individually to handle different statuses for BGM vs regular samples
        const updatePromises = bulkUpdateData.map(async (updateData) => {
          try {
            await samplesService.updateSample(updateData.id, {
              metadonnees: updateData.metadonnees,
              statut: updateData.status,
              commentaires: updateData.commentaires
            });
            return { success: true, id: updateData.id };
          } catch (error) {
            return { success: false, id: updateData.id, error: error.message };
          }
        });
        
        const results = await Promise.all(updatePromises);
        const errors = results.filter(r => !r.success).map(r => `Sample ${r.id}: ${r.error}`);
        result = { 
          updated: results.filter(r => r.success).length, 
          errors 
        };
      } else {
        // Regular AU samples: use bulk update
      const statusUpdate = {
        status: SampleStatus.VALIDATION_SERVICE,
        comment: 'Analyse AU LOT terminée'
      };
        result = await samplesService.updateSampleBatch(sampleIds, metadataUpdates, statusUpdate);
      }

      if (result.errors.length > 0) {
        console.warn(`⚠️ Some errors during bulk analysis completion:`, result.errors);
      }

      console.log(`✅ AU Batch ${selectedBatch.batchNumber} analysis completed - ${result.updated} samples moved to validation`);
      
      // 🚨 UX FIX: Show success message and clean up properly
      setBatchAnalysisDialog(false);
      setSelectedBatch(null);
      setBatchAnalysisData({});
      setSampleDilutionChoices({});
      
      // Show success notification before refresh
      setSnackbarMessage(`✅ Analyse AU du lot ${selectedBatch.batchNumber} complétée - ${result.updated} échantillons envoyés en validation AU`);
      setSnackbarOpen(true);
      
      // Small delay to let user see the success message before refresh
      setTimeout(() => {
        loadAUAnalysisData();
      }, 1000);
      
    } catch (error: any) {
      console.error('Error completing batch analysis:', error);
      setError(`Erreur lors de la finalisation du lot: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 NEW: Send batch back to chemical preparation (SEQUENTIAL - WORKS!)
  const handleSendBatchToPrepChimique = useCallback(async (batchNumber: string, batchSamples: Sample[]) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir renvoyer le lot ${batchNumber} vers la préparation chimique AU ?`)) {
      return;
    }
    
    try {
      setSendingToPrepBatch(true);
      
      console.log(`🔄 Sending batch ${batchNumber} back to AU prep chimique...`);
      
      // 🔧 FIXED: Sequential processing - prevents race conditions
      for (const sample of batchSamples) {
        await samplesService.updateSampleStatus(
              sample.id, 
              SampleStatus.LABORATOIRE_AU_PREP_CHIMIQUE, 
              `Renvoyé vers préparation chimique AU - Lot: ${batchNumber}`
        );
        
        // 🔧 CRITICAL FIX: Reset preparation flags for re-preparation (handles both BGM and non-BGM)
        await samplesService.updateSample(sample.id, {
              metadonnees: {
                ...sample.metadonnees,
                au_analysis_data: null,  // Clear analysis data
                au_preparation_completed: false,  // Reset prep completion flag
                requires_repreparation: true,  // Flag for re-preparation
                repreparation_reason: 'Renvoyé depuis Analyste AU',
                repreparation_requested_at: new Date().toISOString(),
              }
        });
      }
      
      // Reload the data to reflect changes
      await loadAUAnalysisData();
      
      console.log(`✅ Successfully sent batch ${batchNumber} back to AU prep chimique`);
      
      setSnackbarMessage(`Lot ${batchNumber} renvoyé vers la préparation chimique AU avec succès`);
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error('❌ Erreur lors du renvoi vers la préparation:', error);
      setSnackbarMessage(`Erreur lors du renvoi: ${error.message || 'Erreur inconnue'}`);
      setSnackbarOpen(true);
    } finally {
      setSendingToPrepBatch(false);
    }
  }, []);

  // AU Directory Scanning and File Import Functions - 🚀 PERFORMANCE OPTIMIZED (FALLBACK)
  const scanAUDirectory = async () => {
    try {
      setImportLoading(true);
      
      // For batch analysis, use all batch samples
      const batchSampleData = selectedBatch?.samples.map(sample => ({ 
        id: sample.id, 
        sample_id: sample.sample_id 
      })) || [];
      
      // 🚀 TRY: Use bulk endpoint first, fallback to old method if it fails
      try {
        const { files } = await apiService.post<{ files: AUFileInfo[] }>('/au/scan-directory-with-matches', {
          batchSamples: batchSampleData
        });
        setAvailableFiles(files);
      } catch (bulkError) {
        console.log('Bulk endpoint failed, using fallback method...');
        
        // FALLBACK: Use old method and scan all files to avoid missing batch files
        const { files } = await apiService.get<{ files: AUFileInfo[] }>('/au/scan-directory');
        const filesWithMatches = await Promise.all(
          files.map(async (file: any) => {
            const matches = await checkAUFileMatches(file.path, batchSampleData);
            return {
              filename: file.name,
              path: file.path,
              size: file.size,
              modified: file.modified,
              matchedSamples: matches
            };
          })
        );
        setAvailableFiles(filesWithMatches);
      }
      
      setFileImportDialogOpen(true);
    } catch (error) {
      console.error('Error scanning AU directory for batch:', error);
      setError('Erreur lors du scan du répertoire AU pour le lot');
    } finally {
      setImportLoading(false);
    }
  };
  
  const checkAUFileMatches = async (filePath: string, sampleData: Array<{id: string, sample_id: string}>): Promise<string[]> => {
    try {
      const { matches } = await apiService.post<{ matches: string[] }>('/au/check-file-matches', {
        filePath,
        batchSamples: sampleData
      });
      return matches;
    } catch (error) {
      console.error('Error checking AU file matches:', error);
      return [];
    }
  };

  const handleImportAUFile = async (filename: string) => {
    try {
      setImportLoading(true);
      
      if (selectedBatch) {
        // Batch import mode
        const batchSampleData = selectedBatch.samples.map(sample => ({ 
          id: sample.id, 
          sample_id: sample.sample_id 
        }));
        
        // Parse AU file and get results
        const response = await apiService.post<{ 
          results: any[]; 
          totalSamples: number; 
          totalElements: number;
        }>('/au/parse-file', {
          filename,
          batchSamples: batchSampleData
        });

        const { results } = response;
        
        // 🚨 CRITICAL FIX: Merge with existing data instead of overwriting (for split batch support)
        const newBatchData: BatchAnalysisData = { ...batchAnalysisData }; // Start with existing data
        
        // Only initialize missing samples, don't overwrite existing ones
        // 🔥 FIX: Initialize ALL elements that sample needs, not just Au/Ag
        selectedBatch?.samples.forEach(sample => {
          if (!newBatchData[sample.id]) {
            const elementsForThisSample = getElementsForSample(sample);
            newBatchData[sample.id] = Object.fromEntries(
              elementsForThisSample.map(element => [
                element, 
                { grams_per_tonne: 0, ppm: 0, timestamp: new Date().toISOString() }
              ])
            );
          }
        });
        
        // Override with actual imported values
        results.forEach((result: any) => {
          const sample = selectedBatch.samples.find(s => s.sample_id === result.sample_reference);
          
          // 🔥 FIX: Use dynamic elements from sample's AU routing, not hardcoded list
          const allowedElements = sample ? getElementsForSample(sample) : [];
        
        if (sample && allowedElements.includes(result.element)) {
            // Initialize the element slot if it doesn't exist
            if (!newBatchData[sample.id][result.element]) {
              newBatchData[sample.id][result.element] = { grams_per_tonne: 0, ppm: 0, timestamp: new Date().toISOString() };
            }
            
            newBatchData[sample.id][result.element] = {
              lecture: result.reading_value, // Store raw machine reading as 'lecture'
              grams_per_tonne: result.concentration_ppm, // AU results are typically in g/t which equals ppm
              ppm: result.concentration_ppm,
              timestamp: result.timestamp,
              originalReading: result.reading_value, // Keep for compatibility
              calculatedFromFormula: true
            };
            
            console.log(`✅ Imported ${result.element} for ${sample.sample_id}: ${result.concentration_ppm} ppm`);
          } else if (sample) {
            console.log(`⚠️ Skipped ${result.element} for ${sample.sample_id} - not in allowed elements: ${allowedElements.join(', ')}`);
          }
        });
        
        setBatchAnalysisData(newBatchData);
        setSnackbarMessage(`✅ Import réussi! ${results.length} résultats AU importés`);
        setSnackbarOpen(true);
        setFileImportDialogOpen(false);
      }
      
    } catch (error) {
      console.error('Error importing AU file:', error);
      setError('Erreur lors de l\'import du fichier AU');
    } finally {
      setImportLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement des données AU...</Typography>
      </Box>
    );
  }

  if (error) {
  return (
      <Alert severity="error" sx={{ m: 2 }}>
          {error}
        <Button onClick={loadAUAnalysisData} sx={{ ml: 2 }}>
          Réessayer
        </Button>
        </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalFireDepartment sx={{ fontSize: '2rem', color: '#e65100' }} />
                      🧪 Analyse AU - {user?.firstName} {user?.lastName}
                  </Typography>
        
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: '#fff3e0', border: '1px solid #ffb74d' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {stats.ready_for_analysis}
                  </Typography>
                <Typography variant="body2" color="text.secondary">
                  Prêts Analyse AU
                  </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ backgroundColor: '#ffebee', border: '1px solid #ef5350' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="error.main" fontWeight="bold">
                    {stats.urgent_samples}
                  </Typography>
                <Typography variant="body2" color="text.secondary">
                  Urgents
                </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
                </Box>

      {/* 🔍 SEARCH FIELD */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Rechercher par N° Lot ou N° Échantillon..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ 
            width: 400,
            backgroundColor: 'white',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {debouncedSearch && (
          <Chip 
            label={`Recherche: "${debouncedSearch}"`}
            onDelete={() => setSearchTerm('')}
            color="primary"
            variant="outlined"
          />
        )}
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadAUAnalysisData}
          disabled={loading}
        >
          Actualiser
        </Button>
                </Box>

      {/* Samples List */}
      <Card>
            <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Analytics />
            📊 Échantillons pour Analyse AU ({samples.length})
              </Typography>
          
          {samples.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Analytics sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Aucun échantillon prêt pour analyse AU
            </Typography>
              <Typography variant="body2" color="text.secondary">
                Les échantillons apparaîtront ici après la préparation chimique AU.
              </Typography>
          </Box>
          ) : (
            <>
            <TableContainer>
              <Table>
              <TableHead>
                <TableRow>
                    <TableCell>Échantillon</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Priorité</TableCell>
                  <TableCell>Méthodes</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                  {(() => {
                    const { batches, individual } = groupSamplesByBatch(samples);
                    const items: Array<{type: 'batch', batchNumber: string, samples: Sample[]} | {type: 'individual', sample: Sample}> = [];
                    
                    // Sort batches by creation date (oldest first - FIFO workflow)
                    Object.entries(batches)
                      .sort(([, samplesA], [, samplesB]) => {
                        const dateA = new Date(samplesA[0]?.date_creation || 0).getTime();
                        const dateB = new Date(samplesB[0]?.date_creation || 0).getTime();
                        return dateA - dateB;
                      })
                      .forEach(([batchNumber, batchSamples]) => {
                        items.push({ type: 'batch', batchNumber, samples: batchSamples });
                      });
                    individual.forEach(sample => {
                      items.push({ type: 'individual', sample });
                    });

                    // 🚀 Apply frontend pagination
                    const paginatedItems = items.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
                    
                    // Store total for pagination controls
                    if (items.length !== totalBatches) {
                      setTimeout(() => setTotalBatches(items.length), 0);
                    }

                    return paginatedItems.map((item, index) => {
                      if (item.type === 'batch') {
                        const isExpanded = expandedBatches.has(item.batchNumber);
                        
                        return (
                          <React.Fragment key={item.batchNumber}>
                            <TableRow sx={{ backgroundColor: '#fff3e0', borderLeft: '4px solid #e65100' }}>
                    <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => toggleBatchExpansion(item.batchNumber)}
                                  >
                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                  <Box>
                                    <Box sx={{ 
                                      backgroundColor: '#e65100', 
                                      color: 'white', 
                                      px: 1, 
                                      py: 0.5, 
                                      borderRadius: 1,
                                      fontWeight: 'bold'
                                    }}>
                                      LOT AU
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                                      {item.batchNumber}
                                    </Typography>
                      <Chip
                                      label={`${item.samples.length} échantillons`} 
                        size="small"
                                      color="warning" 
                                      variant="outlined"
                      />
                      
                      {/* 🆕 ENHANCED: AU File availability indicator with completion status */}
                      {(() => {
                        const fileStatus = item.samples[0]?.metadonnees?.file_status?.AU;
                        const completionStatus = fileStatus?.completionStatus || 'missing';
                        const fileName = fileStatus?.fileName;
                        const matchCount = fileStatus?.matchCount || 0;
                        const totalSamples = fileStatus?.totalSamples || item.samples.length;
                        const completionPercent = fileStatus?.completionPercent || 0;
                        
                        if (completionStatus === 'complete') {
                          return (
                            <Chip 
                              label={`✅ Fichier complet: ${fileName} (${matchCount}/${totalSamples})`}
                              color="success"
                              variant="filled"
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                                color: 'white',
                                '@keyframes pulse': {
                                  '0%': { opacity: 1 },
                                  '50%': { opacity: 0.7 },
                                  '100%': { opacity: 1 }
                                },
                                animation: 'pulse 2s infinite',
                                boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.4)}`,
                              }}
                            />
                          );
                        } else if (completionStatus === 'partial') {
                          return (
                            <Chip 
                              label={`⚠️ Fichier incomplet: ${fileName} (${matchCount}/${totalSamples} - ${completionPercent}%)`}
                              color="warning"
                              variant="filled"
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                background: `linear-gradient(45deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                                color: 'white',
                                boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.4)}`,
                              }}
                            />
                          );
                        } else {
                          return (
                            <Chip 
                              label="⏳ En attente de fichier"
                              color="default"
                              variant="outlined"
                              size="small"
                              sx={{
                                fontWeight: 'bold',
                                borderColor: theme.palette.grey[400],
                                color: theme.palette.grey[600],
                              }}
                            />
                          );
                        }
                      })()}
                                  </Box>
                                </Box>
                    </TableCell>
                              <TableCell>{item.samples[0]?.client?.company_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                                  label={item.samples.some(s => s.priorite === Priority.URGENTE) ? 'URGENTE' : 'NORMALE'}
                        size="small"
                                  color={item.samples.some(s => s.priorite === Priority.URGENTE) ? 'error' : 'default'}
                      />
                    </TableCell>
                              <TableCell>Analyse AU</TableCell>
                    <TableCell>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  startIcon={analyzingBatch ? <CircularProgress size={16} /> : <Analytics />}
                                  disabled={analyzingBatch}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await handleStartBatchAnalysis(item.batchNumber, item.samples);
                                  }}
                                  sx={{ ml: 'auto' }}
                                >
                                  {analyzingBatch ? 'Préparation...' : 'Analyser le Lot'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  startIcon={sendingToPrepBatch ? <CircularProgress size={16} /> : <Settings />}
                                  disabled={sendingToPrepBatch}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSendBatchToPrepChimique(item.batchNumber, item.samples);
                                  }}
                                  sx={{ 
                                    ml: 1,
                                    '&:hover': {
                                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                      borderColor: theme.palette.warning.main,
                                    }
                                  }}
                                >
                                  {sendingToPrepBatch ? 'Envoi...' : 'Vers Prep'}
                                </Button>
                    </TableCell>
                            </TableRow>

                            {/* Individual Sample Rows - Only show when expanded */}
                            {isExpanded && item.samples.map((sample, sampleIndex) => (
                              <TableRow 
                                key={sample.id}
                                sx={{ 
                                  backgroundColor: '#fff8f0',
                                  borderLeft: '2px solid #e65100'
                                }}
                              >
                                <TableCell sx={{ pl: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" fontWeight="bold">
                                      {sample.sample_id}
                        </Typography>
                                    <SampleQcStatusChip sampleId={sample.id} compact />
                                    <Chip 
                                      label={`Sac ${getBatchPosition(sample)}/${getTotalSamplesInBatch(sample)}`}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                      </Box>
                    </TableCell>
                                <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={sample.priorite}
                                    size="small"
                                    color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>Analyse AU</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                                    variant="outlined"
                                    startIcon={<Analytics />}
                                    onClick={() => {
                                      setSelectedSample(sample);
                                      setAnalysisDialog(true);
                                    }}
                                  >
                                    Analyser Individuel
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                          </React.Fragment>
                        );
                      } else {
                        // Individual sample
                        const sample = item.sample;
                        return (
                          <TableRow key={sample.id}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {sample.sample_id}
                              </Typography>
                            </TableCell>
                            <TableCell>{sample.client?.company_name || 'N/A'}</TableCell>
                            <TableCell>
                              <Chip
                                label={sample.priorite}
                                size="small"
                                color={sample.priorite === Priority.URGENTE ? 'error' : 'default'}
                              />
                            </TableCell>
                            <TableCell>Analyse AU</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Analytics />}
                                onClick={() => {
                                  setSelectedSample(sample);
                                  setAnalysisDialog(true);
                                }}
                              >
                                Analyser
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    });
                  })()}
              </TableBody>
            </Table>
            </TableContainer>
            
            {/* 🚀 PAGINATION CONTROLS */}
            <TablePagination
              component="div"
              count={totalBatches}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100, 250]}
              labelRowsPerPage="Lots par page:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`} lots`
              }
              sx={{
                borderTop: '1px solid #e0e0e0',
                backgroundColor: '#f5f5f5',
              }}
            />
            </>
          )}
        </CardContent>
      </Card>

      {/* Batch Analysis Dialog - COMPLETE SAA-STYLE WITH DILUTION CHOICES */}
      <Dialog 
        open={batchAnalysisDialog} 
        onClose={() => setBatchAnalysisDialog(false)} 
        maxWidth={false}
        fullScreen
        PaperProps={{ 
          sx: { 
            backgroundColor: '#f8f9fa',
            '& .MuiDialogTitle-root': {
              backgroundColor: '#e65100',
              color: 'white'
            }
          } 
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <LocalFireDepartment sx={{ fontSize: '1.8rem' }} />
              <Typography variant="h5" fontWeight="bold">
                Analyse Analyse AU - Lot {selectedBatch?.batchNumber}
              </Typography>
              <Chip 
                label={`${selectedBatch?.samples.length || 0} échantillons`} 
                color="warning" 
                variant="filled"
                sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}
              />
            </Box>
            <Button
              onClick={() => setBatchAnalysisDialog(false)}
              sx={{ color: 'white', minWidth: 'auto', p: 1 }}
            >
              ✕
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
          <Box sx={{ mt: 1 }}>

            {/* AU File Import Section */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                  📁 Import automatique fichiers AU
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={importLoading ? <CircularProgress size={16} /> : <CloudUpload />}
                  onClick={scanAUDirectory}
                  disabled={importLoading}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {importLoading ? 'Scan...' : 'Scanner & Importer'}
                </Button>
              </Box>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Scanne le répertoire AU et applique automatiquement les formules de dilution AU
              </Typography>
            </Box>

            {/* Batch Analysis Results Entry */}
            <Card>
              <CardContent sx={{ p: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    🔥 Saisie des Résultats AU - Lot {selectedBatch?.batchNumber}
                  </Typography>
                  
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {/* 🔧 FIX: Use ALL batch elements (union) for header chips */}
                    {getAllBatchElements(selectedBatch?.samples || []).map((element: string) => (
                      <Chip 
                        key={element} 
                        label={element} 
                        size="small"
                        color="warning" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Formula Display */}
                <Alert severity="info" sx={{ mb: 2, py: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                    🧮 Formule Analyse AU: <strong>Concentration (g/t) = Lecture × (V/p) × (vf/vp)</strong> | V = volume initial, p = poids initial, vf = volume final, vp = volume prélevé
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', mt: 0.5 }}>
                    🔥 Conversion finale: <strong>1 g/t = 1 ppm</strong> | Import automatique applique la formule | <strong>⚗️ Choix dilution: 0 (simplifié) ou 1 (complet) ou 2 (alternatif)</strong>
                  </Typography>
                </Alert>

                {/* Analysis Results Table with Dilution Choices */}
                <Box sx={{ 
                  maxHeight: 'calc(100vh - 200px)', 
                  overflow: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: 1
                }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: '#e65100', color: 'white', fontSize: '0.8rem', py: 1 } }}>
                        <TableCell rowSpan={2} sx={{ verticalAlign: 'middle', minWidth: '100px' }}>
                          Échantillon
                        </TableCell>
                        
                        {/* AU Preparation Data - Exactly like SAA */}
                        <TableCell colSpan={6} align="center" sx={{ backgroundColor: '#ff9800', borderLeft: '2px solid #ff6f00' }}>
                          Variables Formule AU
                        </TableCell>
                        

                        {/* 🔧 FIX: Use ALL batch elements (union) for table headers */}
                        {getAllBatchElements(selectedBatch?.samples || []).map((element: string) => (
                          <TableCell 
                            key={element} 
                            colSpan={4} 
                            align="center" 
                            sx={{ borderLeft: '2px solid #e65100', minWidth: '240px' }}
                          >
                            {element}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow sx={{ '& .MuiTableCell-head': { backgroundColor: '#ff9800', color: 'white', fontSize: '0.7rem', py: 0.5 } }}>
                        {/* Variables de la formule AU - Exactly like SAA */}
                        <TableCell align="center" sx={{ backgroundColor: '#ff9800', minWidth: '60px' }}>
                          V (mL)
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#ff9800', minWidth: '60px' }}>
                          p (g)
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#ff9800', minWidth: '60px' }}>
                          vf (mL)
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#ff9800', minWidth: '60px' }}>
                          vp (mL)
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#ff9800', minWidth: '60px' }}>
                          vf1 (mL)
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#ff9800', minWidth: '60px' }}>
                          vp1 (mL)
                        </TableCell>
                        
                        {/* 🔧 FIX: Use ALL batch elements (union) for sub-header columns */}
                        {getAllBatchElements(selectedBatch?.samples || []).map((element: string) => (
                          <React.Fragment key={element}>
                            <TableCell align="center" sx={{ borderLeft: '1px solid #e65100', minWidth: '60px', backgroundColor: '#e8f5e8' }}>
                              L-{element}
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: '70px', backgroundColor: '#e8f5e8' }}>
                              g/t
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: '70px', backgroundColor: '#e3f2fd' }}>
                              ppm
                            </TableCell>
                            <TableCell align="center" sx={{ minWidth: '50px', backgroundColor: '#f3e5f5' }}>
                              Choix
                            </TableCell>
                          </React.Fragment>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedBatch?.samples
                        .sort((a, b) => {
                          const aNum = parseInt(a.sample_id.replace(/\D/g, ''));
                          const bNum = parseInt(b.sample_id.replace(/\D/g, ''));
                          if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
                          return a.sample_id.localeCompare(b.sample_id);
                        })
                        .map((sample, index) => {
                        // Initialize data based on sample type (BGM = Au only, Regular = Au + Ag)
                        const elementsForSample = getElementsForSample(sample);
                        const defaultData = Object.fromEntries(
                          elementsForSample.map(element => [
                            element, 
                            { 
                              lecture: 0,
                              grams_per_tonne: 0, 
                              ppm: 0, 
                              timestamp: new Date().toISOString(),
                              originalReading: 0,
                              calculatedFromFormula: false
                            }
                          ])
                        );
                        const sampleData = batchAnalysisData[sample.id] || defaultData;
                        

                        
                        return (
                          <TableRow 
                            key={sample.id}
                            sx={{ 
                              backgroundColor: index % 2 === 0 ? '#fff8f0' : 'white',
                              '&:hover': { backgroundColor: '#ffecc7' }
                            }}
                          >
                            {/* Sample ID */}
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                {sample.sample_id}
                              </Typography>
                            </TableCell>

                            {/* AU Preparation Data - All 6 columns exactly like SAA */}
                            <TableCell align="center" sx={{ backgroundColor: '#fff8f0' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {sample.metadonnees?.au_preparation?.initialVolume ?? 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ backgroundColor: '#fff8f0' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {sample.metadonnees?.au_preparation?.initialWeight ?? 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ backgroundColor: '#fff8f0' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {sample.metadonnees?.au_preparation?.finalVolume ?? 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ backgroundColor: '#fff8f0' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {sample.metadonnees?.au_preparation?.volumeTaken ?? 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ backgroundColor: '#fff8f0' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {sample.metadonnees?.au_preparation?.vf1 ?? 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center" sx={{ backgroundColor: '#fff8f0' }}>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {sample.metadonnees?.au_preparation?.vp1 ?? 'N/A'}
                              </Typography>
                            </TableCell>



                            {/* Elements Results - 🔧 FIX: Iterate over ALL batch elements, grey out non-selected */}
                            {getAllBatchElements(selectedBatch?.samples || []).map((element: string) => {
                              const hasElement = elementsForSample.includes(element);
                              
                              // 🔧 FIX: Show greyed cells for elements not in this sample
                              if (!hasElement) {
                                return (
                                  <React.Fragment key={element}>
                                    <TableCell sx={{ borderLeft: '1px solid #ff9800', p: 0.5, backgroundColor: '#f5f5f5' }} colSpan={4}>
                                      <Typography sx={{ color: '#bdbdbd', textAlign: 'center', fontSize: '0.75rem' }}>-</Typography>
                                    </TableCell>
                                  </React.Fragment>
                                );
                              }
                              
                              return (
                              <React.Fragment key={element}>
                                {/* Lecture Column - Display Only */}
                                <TableCell sx={{ borderLeft: '1px solid #ff9800', p: 0.5, backgroundColor: '#fff8f0' }}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontSize: '0.75rem', 
                                      textAlign: 'center',
                                      fontWeight: sampleData[element]?.calculatedFromFormula ? 'bold' : 'normal',
                                      color: sampleData[element]?.calculatedFromFormula ? '#ff6f00' : 'text.primary'
                                    }}
                                  >
                                    {(() => {
                                      const lectureValue = sampleData[element]?.lecture || sampleData[element]?.originalReading;
                                      return (lectureValue !== undefined && lectureValue !== null) 
                                        ? lectureValue.toFixed(4) 
                                        : '-';
                                    })()}
                                  </Typography>
                                  {sampleData[element]?.calculatedFromFormula && (
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#ff9800', display: 'block' }}>
                                      Import
                                    </Typography>
                                  )}
                                </TableCell>

                                {/* g/t Column - EDITABLE */}
                                <TableCell sx={{ p: 0.5 }}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={sampleData[element]?.grams_per_tonne || ''}
                                    onChange={(e) => {
                                      const newValue = parseFloat(e.target.value) || 0;
                                      updateBatchAnalysisData(sample.id, element, {
                                        ...sampleData[element],
                                        grams_per_tonne: newValue,
                                        ppm: newValue, // Keep in sync (1 g/t = 1 ppm for AU)
                                        timestamp: new Date().toISOString()
                                      });
                                    }}
                                    inputProps={{ 
                                      step: 0.001, 
                                      min: 0,
                                      style: { 
                                        textAlign: 'center', 
                                        fontSize: '0.75rem',
                                        fontWeight: 'bold',
                                        padding: '4px'
                                      }
                                    }}
                                    sx={{ 
                                      width: '80px',
                                      '& .MuiOutlinedInput-root': {
                                        backgroundColor: sampleData[element]?.calculatedFromFormula ? '#e8f5e9' : '#fff',
                                        border: sampleData[element]?.calculatedFromFormula ? '2px solid #4caf50' : '1px solid #ddd',
                                        '&:hover': {
                                          border: '2px solid #2196f3'
                                        },
                                        '&.Mui-focused': {
                                          border: '2px solid #1976d2',
                                          backgroundColor: '#e3f2fd'
                                        }
                                      },
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                      }
                                    }}
                                  />
                                </TableCell>

                                {/* ppm Column - EDITABLE */}
                                <TableCell sx={{ p: 0.5 }}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={sampleData[element]?.ppm || ''}
                                    onChange={(e) => {
                                      const newValue = parseFloat(e.target.value) || 0;
                                      updateBatchAnalysisData(sample.id, element, {
                                        ...sampleData[element],
                                        grams_per_tonne: newValue, // Keep in sync
                                        ppm: newValue,
                                        timestamp: new Date().toISOString()
                                      });
                                    }}
                                    inputProps={{ 
                                      step: 0.001, 
                                      min: 0,
                                      style: { 
                                        textAlign: 'center', 
                                        fontSize: '0.75rem',
                                        fontWeight: 'bold',
                                        padding: '4px'
                                      }
                                    }}
                                    sx={{ 
                                      width: '80px',
                                      '& .MuiOutlinedInput-root': {
                                        backgroundColor: sampleData[element]?.calculatedFromFormula ? '#e3f2fd' : '#fff',
                                        border: sampleData[element]?.calculatedFromFormula ? '2px solid #2196f3' : '1px solid #ddd',
                                        '&:hover': {
                                          border: '2px solid #2196f3'
                                        },
                                        '&.Mui-focused': {
                                          border: '2px solid #1976d2',
                                          backgroundColor: '#e3f2fd'
                                        }
                                      },
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                      }
                                    }}
                                  />
                                </TableCell>

                                {/* Choix Column */}
                                <TableCell>
                                  <TextField
                                    size="small"
                                    value={sampleDilutionChoices[sample.id]?.[element] ?? 0}
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value);
                                      if ([0, 1, 2].includes(value)) {
                                        setSampleDilutionChoices(prev => ({
                                          ...prev,
                                          [sample.id]: {
                                            ...prev[sample.id],
                                            [element]: value as 0 | 1 | 2
                                          }
                                        }));
                                        
                                        // Recalculate with new choice
                                        const sampleData = batchAnalysisData[sample.id]?.[element];
                                        const lecture = sampleData?.lecture || 0;
                                        const prepData = sample.metadonnees?.au_preparation;
                                        const V = prepData?.initialVolume || 200;
                                        const p = prepData?.initialWeight || 0.55;
                                        const vf = prepData?.finalVolume || 1;
                                        const vp = prepData?.volumeTaken || 1;
                                        const vf1 = prepData?.vf1 || 1;
                                        const vp1 = prepData?.vp1 || 1;
                                        
                                        let grams_per_tonne = 0;
                                        if (value === 0) {
                                          grams_per_tonne = lecture * (V / p);
                                        } else if (value === 1) {
                                          grams_per_tonne = lecture * (V / p) * (vf / vp);
                                        } else if (value === 2) {
                                          grams_per_tonne = lecture * (V / p) * (vf1 / vp1);
                                        }
                                        
                                        updateBatchAnalysisData(sample.id, element, {
                                          ...sampleData,
                                          grams_per_tonne,
                                          ppm: grams_per_tonne,
                                          timestamp: new Date().toISOString()
                                        });
                                      }
                                    }}
                                    inputProps={{ 
                                      style: { 
                                        textAlign: 'center', 
                                        fontSize: '0.7rem', 
                                        fontWeight: 'bold',
                                        padding: '2px'
                                      },
                                      maxLength: 1
                                    }}
                                    sx={{ 
                                      width: '40px',
                                      '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#f3e5f5'
                                      }
                                    }}
                                  />
                                </TableCell>
                              </React.Fragment>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                        </Box>
                  </CardContent>
                </Card>
            </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: 'white', borderTop: '1px solid #ddd' }}>
          <Button
            onClick={() => setBatchAnalysisDialog(false)}
            color="inherit"
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
            disabled={loading || !selectedBatch?.samples.length}
            onClick={handleCompleteBatchAnalysis}
          >
            {loading ? 'Finalisation...' : `💾 Finaliser Analyse AU (${selectedBatch?.samples.length || 0} échantillons)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AU File Import Dialog */}
      <Dialog open={fileImportDialogOpen} onClose={() => setFileImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LocalFireDepartment color="warning" />
            Import de fichiers Analyse AU
          </Box>
        </DialogTitle>
        <DialogContent>
          {availableFiles && availableFiles.length > 0 ? (
            <List>
              {availableFiles.map((file: AUFileInfo, index: number) => (
                <ListItem key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <ListItemText
                    primary={file.filename}
                    secondary={`${file.matchedSamples.length} échantillons correspondants`}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleImportAUFile(file.filename)}
                    disabled={file.matchedSamples.length === 0}
                  >
                    Importer
                  </Button>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>Aucun fichier AU trouvé dans le répertoire</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFileImportDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default AUAnalystDashboard; 